import { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Result } from 'antd';
import classNames from 'classnames';
import { Flex, Helmet, TextComponent } from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import Breadcrumbs from '../../components/layout/breadcrumbs/breadcrumbs';

import styles from './page-container.module.scss';

export interface PageContainerProps extends PropsWithChildren {
  className?: string;
  mainClassName?: string;
  hederContentClassName?: string;
  headerTopClassName?: string;
  subheaderClassName?: string;
  helmet: string;
  pageTitle?: ReactNode;
  hideTitleOnMobile?: boolean;
  pageTitlePostfix?: ReactNode;
  headerContent?: ReactNode;
  segments?: ReactNode;
  bannerLink?: string;
  isError?: boolean;
  testId?: string;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 *
 */
const PageContainer: FunctionComponent<PageContainerProps> = ({
  helmet,
  className,
  mainClassName = '',
  hederContentClassName = '',
  headerTopClassName = '',
  subheaderClassName = '',
  children,
  pageTitle,
  pageTitlePostfix,
  headerContent,
  isError,
  hideTitleOnMobile,
  testId,
  segments,
  bannerLink,
  scrollRef,
}) => {
  const { isDesktop, isMobile } = useResponsiveness();
  const { t } = useTranslation(['errors']);
  let shouldShowTitle = Boolean(pageTitle);

  if (hideTitleOnMobile && !isDesktop) {
    shouldShowTitle = false;
  }

  const shouldShowHeaderContent =
    shouldShowTitle || pageTitlePostfix || headerContent;

  const mainContent = () => {
    if (isError) {
      return (
        <Result
          status="500"
          title="500"
          subTitle={t('errors:something_went_wrong')}
        />
      );
    }
    return children;
  };

  const isSubHeader = !!pageTitle || !!pageTitlePostfix;

  return (
    <Flex
      vertical
      //  gap={12}
      className={classNames(styles.container, className, {
        [styles.hasBanner]: bannerLink,
      })}
      data-testid={testId}
    >
      <Helmet title={helmet} />

      {/* Header */}
      <Flex vertical gap={isMobile ? 12 : 18} className="responsive-padding-x">
        {/* Header top section */}
        <Flex
          vertical
          gap={isMobile ? 12 : 18}
          className={classNames(styles.headerTop, headerTopClassName)}
        >
          {isMobile && <Breadcrumbs />}
          {segments}
        </Flex>

        {/* Header content section */}
        {shouldShowHeaderContent && (
          <Flex
            gap={5}
            justify="space-between"
            wrap="wrap"
            align="center"
            className={classNames(
              styles.content,
              {
                [styles.contentMobile]: isMobile,
                [styles.contentHasBanner]: bannerLink,
              },
              hederContentClassName,
            )}
            style={{
              backgroundImage: `url(${bannerLink})`,
            }}
          >
            {isSubHeader && (
              <Flex
                align="center"
                gap={8}
                wrap="wrap"
                className={classNames(
                  styles.subheader,
                  {
                    [styles.subheaderMobile]: isMobile,
                  },
                  subheaderClassName,
                )}
              >
                {shouldShowTitle && (
                  <TextComponent
                    color={
                      bannerLink
                        ? 'var(--color-white)'
                        : 'var(--color-text-seed)'
                    }
                    weight={500}
                    level={isDesktop ? 2 : 4}
                  >
                    {pageTitle}
                  </TextComponent>
                )}
                {pageTitlePostfix}
              </Flex>
            )}
            {headerContent}
          </Flex>
        )}
      </Flex>
      {/* Main content */}
      <div
        ref={scrollRef}
        className={classNames(
          styles.main,
          mainClassName,
          'responsive-padding-x',
        )}
      >
        {mainContent()}
      </div>
    </Flex>
  );
};

export default PageContainer;
